/* DepositReleaseList.js — استرداد سپرده در قبال صورت وضعیت
   («پرداخت صورت وضعیت از محل سپرده یا ضمانتنامه»)

   چیدمان:
     صفحه            → فهرست صورت وضعیت‌ها (جدول اصلی)
     پاپ‌آپ سطح ۱     → ریز سپرده‌های همان قرارداد/پیمانکار
     پاپ‌آپ سطح ۲     → ثبت «استرداد فعلی» یک ردیف — قابل جابجایی با درگ
                        تا پاپ‌آپ اول زیرش دیده شود
*/
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TabBar } from '../TabManager';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import useColumnResize from '../../hooks/useColumnResize';
import useTablePagination from '../../hooks/useTablePagination';
import { useFormButtons } from '../../utils/vbPerms';
import { jsonOf, netErr } from '../../utils/http';

/* ── کمکی‌ها ── */
const toFa   = n => n===null||n===undefined?'':String(n).replace(/[0-9]/g,d=>'۰۱۲۳۴۵۶۷۸۹'[d]);
const numFmt = n => { const v=Number(n||0); return v===0?'':v.toLocaleString('fa-IR'); };
const API    = (path,opts={}) => fetch(`/api/deposit${path}`,{
  headers:{'Content-Type':'application/json',Authorization:`Bearer ${localStorage.getItem('access')}`},...opts,
}).then(jsonOf, netErr);

/* زیرکامپوننت‌ها بیرون — وگرنه فوکوس می‌پره */
const FInput = ({label,name,value,onChange,readOnly,flex=1,type='text',dir}) => (
  <div style={{display:'flex',flexDirection:'column',gap:'4px',flex}}>
    <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>{label}</label>
    <input name={name} type={type} value={value??''} onChange={onChange} readOnly={readOnly}
      style={{padding:'8px 12px',border:'1.5px solid #e0e0e0',borderRadius:'8px',fontSize:'13px',
        fontFamily:"'Vazirmatn',Tahoma,sans-serif",outline:'none',
        direction:dir||'rtl',textAlign:dir==='ltr'?'left':'right',
        width:'100%',boxSizing:'border-box',background:readOnly?'#f5f5f5':'#fff'}}/>
  </div>
);

const FSelect = ({label,name,value,onChange,options,flex=1}) => (
  <div style={{display:'flex',flexDirection:'column',gap:'4px',flex}}>
    <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>{label}</label>
    <select name={name} value={value??''} onChange={onChange}
      style={{padding:'8px 12px',border:'1.5px solid #e0e0e0',borderRadius:'8px',fontSize:'13px',
        fontFamily:"'Vazirmatn',Tahoma,sans-serif",outline:'none',direction:'rtl',
        width:'100%',boxSizing:'border-box',background:'#fff'}}>
      {options.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const FDate = ({label,value,onChange,flex=1}) => (
  <div style={{display:'flex',flexDirection:'column',gap:'4px',flex}}>
    <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>{label}</label>
    <JalaliDatePicker value={value} onChange={onChange} placeholder={label}/>
  </div>
);

/* ستون‌های جدول اصلی */
const INIT_WIDTHS = [45,140,220,105,120,180,150,140,140,140,140,150,150,140,140,90];

const EMPTY = {contract_id:0,contract_no:'',contractor:'',invoice_no:'',
  invoice_date:'',invoice_amount:'',on_account:'',pre_payment:'',deposit:'',
  added_tax:'',final_pay:'',pitch:100,act_no:'',
  request_no:'',request_date:'',letter_no:'',letter_date:'',
  new_contract_id:0,new_contract_no:'',new_contract_year:'',zemanat_amount:'',
  new_contract_situation:0, remark:'', is_showable:false};

export default function DepositReleaseList({ user, onBack }) {
  // دکمه‌های فرم FRMRELEASEDEPOSITORZEMANAT؛ دیدن = دسترسی
  const vbf = useFormButtons('FRMRELEASEDEPOSITORZEMANAT', ["BTNEDIT","BTNNEW","BTNDELETE","BTNADDITION","BTNDEDUCTION","BTNOK"]);
  /* ── جدول اصلی ── */
  const [rows,setRows]         = useState([]);
  const [total,setTotal]       = useState(0);
  const [hasMore,setHasMore]   = useState(false);
  const [loading,setLoading]   = useState(false);
  const [selected,setSelected] = useState(null);

  /* ── فیلترها ── */
  const [contractor,setContractor] = useState(0);
  const [contractors,setContractors] = useState([]);
  const [openRefunds,setOpenRefunds] = useState([]);
  const [ready,setReady] = useState(false);

  /* ── مودال‌ها ── */
  const [modal,setModal]   = useState(null);   // new | edit | delete
  const [form,setForm]     = useState(EMPTY);
  const [error,setError]   = useState('');
  const [toast,setToast]   = useState('');
  const [lookup,setLookup] = useState([]);

  /* ── پاپ‌آپ سطح ۱: ریز سپرده‌ها ── */
  const [rowsOpen,setRowsOpen]   = useState(false);
  const [depRows,setDepRows]     = useState([]);
  const [depSums,setDepSums]     = useState({sum_deposit:0,sum_current:0,sum_remain:0});
  const [depBy,setDepBy]         = useState('contractor');   // contractor | contract
  const [depLoading,setDepLoading] = useState(false);

  /* ── پاپ‌آپ اضافات و کسورات ── */
  const [itemModal,setItemModal] = useState(null);   // addition | deduction
  const [items,setItems]         = useState([]);
  const [itemSum,setItemSum]     = useState(0);
  const [itemTypes,setItemTypes] = useState([]);
  const [itemForm,setItemForm]   = useState({id:0,kind_id:0,amount:'',percent:''});
  const [itemError,setItemError] = useState('');

  /* ── پاپ‌آپ سطح ۲: ثبت استرداد فعلی (قابل جابجایی) ── */
  const [alloc,setAlloc]       = useState(null);   // ردیف در حال ویرایش
  const [allocPay,setAllocPay] = useState('');
  const [allocRefund,setAllocRefund] = useState(0);
  const [allocError,setAllocError]   = useState('');
  const [pos,setPos] = useState({x:0,y:0});
  const dragRef = useRef({on:false,dx:0,dy:0});

  const offsetRef = useRef(0);

  const {TH} = useColumnResize(INIT_WIDTHS);
  const {visibleRows,search,setSearch,totalCount,LoadMoreButton} = useTablePagination({
    allRows:rows, searchFields:['contract_no','contractor','invoice_no','contract_subject'],
    serverTotal:total, serverHasMore:hasMore,
  });

  const showToast = msg => { setToast(msg); setTimeout(()=>setToast(''),2800); };

  /* درگ پاپ‌آپ سطح ۲ — با useRef تا کلوژر کهنه نسازد */
  useEffect(()=>{
    const move = e => {
      if(!dragRef.current.on) return;
      setPos({x:e.clientX-dragRef.current.dx, y:e.clientY-dragRef.current.dy});
    };
    const up = ()=>{ dragRef.current.on=false; };
    window.addEventListener('mousemove',move);
    window.addEventListener('mouseup',up);
    return ()=>{ window.removeEventListener('mousemove',move); window.removeEventListener('mouseup',up); };
  },[]);
  const startDrag = e => {
    dragRef.current = {on:true, dx:e.clientX-pos.x, dy:e.clientY-pos.y};
    e.preventDefault();
  };

  /* مقداردهی اولیه */
  useEffect(()=>{ (async()=>{
    try{
      const d = await API('/releases/init/');
      if(d.ok){ setContractors(d.contractors||[]); setOpenRefunds(d.open_refunds||[]); }
    }catch(e){console.error(e);}
    setReady(true);
  })(); },[]);

  const fetchData = useCallback(async(reset=false)=>{
    if(reset){ offsetRef.current=0; setRows([]); setSelected(null); }
    setLoading(true);
    try{
      const off = reset?0:offsetRef.current;
      const d = await API(`/releases/?offset=${off}&contractor_code=${contractor}`);
      if(d.ok){
        setRows(p=>reset?d.rows:[...p,...d.rows]);
        setTotal(d.total); setHasMore(d.has_more);
        offsetRef.current = (reset?0:offsetRef.current)+d.rows.length;
      }
    }catch(e){console.error(e);}
    setLoading(false);
  },[contractor]);

  useEffect(()=>{ if(ready) fetchData(true); },[ready,contractor]); // eslint-disable-line

  const handleChange = e => { const{name,value}=e.target; setForm(p=>({...p,[name]:value})); };

  /* ── پاپ‌آپ ریز سپرده ── */
  const loadDepositRows = useCallback(async(rel,by)=>{
    if(!rel) return;
    setDepLoading(true);
    const d = await API(`/releases/${rel.id}/deposit-rows/?by=${by}`);
    if(d.ok){
      setDepRows(d.rows);
      setDepSums({sum_deposit:d.sum_deposit,sum_current:d.sum_current,sum_remain:d.sum_remain});
    } else alert(d.error||'خطا');
    setDepLoading(false);
  },[]);

  const openRowsPopup = async r => {
    setSelected(r);
    setDepBy(r.release_kind===1?'contract':'contractor');
    setRowsOpen(true);
    await loadDepositRows(r, r.release_kind===1?'contract':'contractor');
  };
  useEffect(()=>{ if(rowsOpen && selected) loadDepositRows(selected,depBy); },[depBy]); // eslint-disable-line

  /* ── پاپ‌آپ تخصیص ── */
  const openAlloc = row => {
    if(row.refund_blocked){
      alert(row.block_reason||'استرداد این ردیف سپرده مجاز نیست'); return;
    }
    setAlloc(row);
    setAllocPay(row.refund_current||'');
    setAllocRefund(openRefunds[0]?.id||0);
    setAllocError('');
    // پاپ‌آپ دوم کمی پایین‌تر و چپ‌تر باز می‌شود تا اولی زیرش دیده شود
    setPos({x:-160,y:110});
  };

  const saveAlloc = async()=>{ if (!vbf.can('BTNEDIT')) return;
    const pay = Number(allocPay||0);
    if(pay > alloc.remain + alloc.refund_current){
      setAllocError('مبلغ استرداد از مانده ریز سپرده بیشتر است'); return;
    }
    if(pay>0 && !alloc.refund_current && !allocRefund){
      setAllocError('برگه استرداد را انتخاب کنید'); return;
    }
    setAllocError('');
    const d = await API(`/releases/${selected.id}/allocate/`,{method:'POST',body:JSON.stringify({
      credit_no:alloc.credit_no, credit_year:alloc.credit_year, detail_row:alloc.detail_row,
      refund_current:pay, refund_id:allocRefund,
    })});
    if(d.ok){
      setAlloc(null);
      await loadDepositRows(selected,depBy);
      fetchData(true);
      showToast(pay===0?'✅ تخصیص حذف شد':'✅ استرداد فعلی ثبت شد');
    } else setAllocError(d.error||'خطا');
  };

  /* ── اضافات و کسورات ── */
  const openItems = async kind => {
    if(!selected) return;
    setItemModal(kind); setItemError('');
    setItemForm({id:0,kind_id:0,amount:'',percent:''});
    const [list,types] = await Promise.all([
      API(`/releases/${selected.id}/items/${kind}/`),
      API(`/item-types/${kind}/`),
    ]);
    if(list.ok){ setItems(list.rows); setItemSum(list.sum); } else setItemError(list.error||'خطا');
    if(types.ok) setItemTypes(types.rows);
  };

  const reloadItems = async kind => {
    const d = await API(`/releases/${selected.id}/items/${kind}/`);
    if(d.ok){ setItems(d.rows); setItemSum(d.sum); }
    fetchData(true);
  };

  const saveItem = async()=>{ if (!vbf.can('BTNNEW')) return;
    if(!Number(itemForm.kind_id)){ setItemError('نوع را انتخاب کنید'); return; }
    if(!Number(itemForm.amount) && !Number(itemForm.percent)){
      setItemError('مبلغ یا درصد را وارد کنید'); return; }
    setItemError('');
    const url = itemForm.id
      ? `/releases/${selected.id}/items/${itemModal}/${itemForm.id}/save/`
      : `/releases/${selected.id}/items/${itemModal}/save/`;
    const d = await API(url,{method:'POST',body:JSON.stringify(itemForm)});
    if(d.ok){
      setItemForm({id:0,kind_id:0,amount:'',percent:''});
      reloadItems(itemModal);
      showToast('✅ ثبت شد');
    } else setItemError(d.error||'خطا');
  };

  const deleteItem = async id => {
    if(!window.confirm('این قلم حذف شود؟')) return;
    const d = await API(`/releases/${selected.id}/items/${itemModal}/${id}/delete/`,
      {method:'POST'});
    if(d.ok){ reloadItems(itemModal); showToast('✅ حذف شد'); }
    else setItemError(d.error||'خطا');
  };

  /* ── جدید / ویرایش سرگروه ── */
  const openNew = ()=>{ if (!vbf.can('BTNNEW')) return; setForm(EMPTY); setError(''); setLookup([]); setModal('new'); };
  const openEdit = ()=>{ if (!vbf.can('BTNEDIT')) return;
    if(!selected) return;
    setForm({...EMPTY, ...selected});
    setError(''); setLookup([]); setModal('edit');
  };

  const validate = f => {
    if(Number(f.contract_id||0)===0)     return 'قرارداد انتخاب نشده است';
    if(!String(f.invoice_no||'').trim()) return 'شماره صورت وضعیت وارد نشده است';
    if(Number(f.invoice_amount||0)===0)  return 'مبلغ صورت وضعیت وارد نشده است';
    return null;
  };

  const handleSave = async()=>{
    const v = validate(form);
    if(v){ setError(v); return; }
    setError('');
    const url = modal==='new' ? '/releases/create/' : `/releases/${selected.id}/update/`;
    const d = await API(url,{method:'POST',body:JSON.stringify(form)});
    if(d.ok){ setModal(null); fetchData(true); showToast(modal==='new'?'✅ ثبت شد':'✅ ویرایش شد'); }
    else setError(d.error||'خطا');
  };

  const openDelete = ()=>{ if (!vbf.can('BTNDELETE')) return; if(!selected) return; setError(''); setModal('delete'); };
  const handleDelete = async()=>{
    const d = await API(`/releases/${selected.id}/delete/`,{method:'POST'});
    if(d.ok){ setModal(null); setSelected(null); fetchData(true); showToast('✅ حذف شد'); }
    else setError(d.error||'خطا');
  };

  const doLookup = async q => {
    const d = await API(`/contracts/lookup/?q=${encodeURIComponent(q||'')}`);
    if(d.ok) setLookup(d.rows);
  };

  /* خالص محاسبه‌شده در مودال */
  /* خالص = ناخالص + اضافات − کسورات − علی الحساب − پیش پرداخت
     اضافات و کسورات از جدول‌های فرزند می‌آیند و در مودال فقط نمایش داده می‌شوند */
  const formAdd = Number(selected?.addition||0);
  const formDed = Number(selected?.deduction||0);
  const formNet = Number(form.invoice_amount||0) + (modal==='edit'?formAdd:0)
    - (modal==='edit'?formDed:0)
    - Number(form.on_account||0) - Number(form.pre_payment||0);

  const contractorOpts = [{value:0,label:'همه پیمانکاران'},
    ...contractors.map(c=>({value:c.code,label:c.title}))];
  const refundOpts = openRefunds.length
    ? openRefunds.map(r=>({value:r.id,label:`برگه ${toFa(r.no)}/${toFa(r.year)} — ${numFmt(r.amount)}`}))
    : [{value:0,label:'برگه استرداد بازی موجود نیست'}];

  return (
    <div style={s.page}>
      <header style={s.header}>
        <div style={s.hLeft}>
          <button onClick={onBack} style={s.backBtn}>← بازگشت</button>
          <div style={s.hLogo}><img src={require('../../assets/images/arjan.png')} alt="" style={{width:'34px',height:'34px',borderRadius:'7px'}}/></div>
          <div><div style={s.hTitle}>استرداد سپرده در قبال صورت وضعیت</div>
               <div style={s.hSub}>پرداخت از محل سپرده یا ضمانتنامه — بخش سپرده</div></div>
        </div>
        <span style={s.userName}>{user?.full_name}</span>
      </header>
      <TabBar/>

      <main style={s.main}>
        {/* ── نوار ابزار ── */}
        <div style={s.toolbar}>
          <div style={s.toolBtns}>
            {vbf.can('BTNNEW') && <button onClick={openNew} style={s.btnNew}>＋ جدید</button>}
            {vbf.can('BTNEDIT') && <button onClick={openEdit} disabled={!selected} style={{...s.btnEdit,opacity:selected?1:.4}}>✎ ویرایش</button>}
            {vbf.can('BTNDELETE') && <button onClick={openDelete} disabled={!selected} style={{...s.btnDel,opacity:selected?1:.4}}>✕ حذف</button>}
            <button onClick={()=>selected&&openRowsPopup(selected)} disabled={!selected}
              style={{...s.btnRows,opacity:selected?1:.4}}>📄 ریز سپرده و استرداد فعلی</button>
            {vbf.can('BTNADDITION') && <button onClick={()=>openItems('addition')} disabled={!selected}
              style={{...s.btnAdd,opacity:selected?1:.4}}>
              ＋ اضافات{selected?.addition_count?` (${toFa(selected.addition_count)})`:''}</button>}
            {vbf.can('BTNDEDUCTION') && <button onClick={()=>openItems('deduction')} disabled={!selected}
              style={{...s.btnDed,opacity:selected?1:.4}}>
              − کسورات{selected?.deduction_count?` (${toFa(selected.deduction_count)})`:''}</button>}
          </div>
        </div>

        {/* ── فیلترها + جستجو ── */}
        <div style={s.toolbar2}>
          <div style={{width:'300px'}}>
            <FSelect label="پیمانکار" name="contractor" value={contractor}
              onChange={e=>setContractor(Number(e.target.value))} options={contractorOpts}/>
          </div>
          <div style={{marginRight:'auto'}}>
            <div style={s.fL}>جستجو</div>
            <input value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="قرارداد / پیمانکار / صورت وضعیت" style={{...s.fI,width:'300px'}}/>
          </div>
        </div>

        {/* ── جدول اصلی: فهرست صورت وضعیت‌ها ── */}
        <div style={s.tableWrap}>
          <table style={{width:'100%',borderCollapse:'collapse',tableLayout:'fixed'}}>
            <thead style={{position:'sticky',top:0,zIndex:3}}>
              <tr>
                <TH idx={0}>ردیف</TH>
                <TH idx={1}>قرارداد</TH>
                <TH idx={2} right>موضوع قرارداد</TH>
                <TH idx={3}>تاریخ</TH>
                <TH idx={4}>صورت وضعیت</TH>
                <TH idx={5} right>پیمانکار</TH>
                <TH idx={6} gold>مبلغ صورت وضعیت</TH>
                <TH idx={7} green>اضافات</TH>
                <TH idx={8} red>کسورات</TH>
                <TH idx={9} red>علی الحساب</TH>
                <TH idx={10} red>پیش پرداخت</TH>
                <TH idx={11} gold>خالص</TH>
                <TH idx={12} gold>قابل پرداخت</TH>
                <TH idx={13} green>سپرده</TH>
                <TH idx={14} green>ضمانتنامه</TH>
                <TH idx={15}>مانده</TH>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length===0 && (
                <tr><td colSpan={16} style={s.empty}>
                  {loading?'در حال بارگذاری...':'اطلاعاتی برای نمایش وجود ندارد'}
                </td></tr>)}
              {visibleRows.map((r,idx)=>{
                const sel = selected?.id===r.id;
                const bg  = sel ? '#64b5f6'
                          : r.remain!==0 ? '#fff8e1'
                          : idx%2===0 ? '#fff' : '#fdf6e3';
                return (
                  <tr key={r.id} onClick={()=>setSelected(r)} onDoubleClick={()=>openRowsPopup(r)}
                    style={{cursor:'pointer',background:bg}}
                    onMouseEnter={e=>{if(!sel)e.currentTarget.style.background='#bbdefb';}}
                    onMouseLeave={e=>{if(!sel)e.currentTarget.style.background=bg;}}>
                    <td style={s.td}>{toFa(idx+1)}</td>
                    <td style={s.td}>{toFa(r.contract_no)}</td>
                    <td style={{...s.td,textAlign:'right'}} title={r.contract_subject}>{r.contract_subject}</td>
                    <td style={{...s.td,direction:'ltr'}}>{toFa(r.contract_date)}</td>
                    <td style={s.td}>{toFa(r.invoice_no)}</td>
                    <td style={{...s.td,textAlign:'right'}} title={r.contractor}>{r.contractor}</td>
                    <td style={{...s.td,...s.num}}>{numFmt(r.invoice_amount)}</td>
                    <td style={{...s.td,...s.num,color:'#1b5e20'}}>{numFmt(r.addition)}</td>
                    <td style={{...s.td,...s.num,color:'#c62828'}}>{numFmt(r.deduction)}</td>
                    <td style={{...s.td,...s.num,color:'#c62828'}}>{numFmt(r.on_account)}</td>
                    <td style={{...s.td,...s.num,color:'#c62828'}}>{numFmt(r.pre_payment)}</td>
                    <td style={{...s.td,...s.num,color:'#6a1b9a'}} title={r.net_mismatch
                      ?`مقدار ذخیره‌شده: ${numFmt(r.net_stored)}`:''}>
                      {numFmt(r.net)}{r.net_mismatch?' ⚠':''}</td>
                    <td style={{...s.td,...s.num,color:'#0d47a1'}}>{numFmt(r.final_pay)}</td>
                    <td style={{...s.td,...s.num,color:'#1565c0'}}>{numFmt(r.deposit)}</td>
                    <td style={{...s.td,...s.num,color:'#00695c'}}>{numFmt(r.zemanat_amount)}</td>
                    <td style={{...s.td,...s.num,
                      color:r.remain!==0?'#e65100':'#999'}}>{numFmt(r.remain)}</td>
                  </tr>);
              })}
            </tbody>
          </table>
        </div>
        <LoadMoreButton onClick={()=>fetchData(false)} loading={loading}/>

        <div style={s.summary}>
          <span>تعداد: <b>{toFa(visibleRows.length)}</b> از <b>{toFa(totalCount)}</b></span>
          {selected && <>
            <span>انتخابی: <b>{selected.contractor}</b> — صورت وضعیت <b>{toFa(selected.invoice_no)}</b></span>
            <span>جمع: <b>{numFmt(selected.total_amount)||'۰'}</b></span>
            <span>خالص: <b style={{color:'#6a1b9a'}}>{numFmt(selected.net)||'۰'}</b></span>
            <span>سپرده: <b style={{color:'#1565c0'}}>{numFmt(selected.deposit)||'۰'}</b></span>
            <span>تخصیص‌یافته از ریز سپرده: <b style={{color:'#0d47a1'}}>{numFmt(selected.allocated)||'۰'}</b></span>
            <span>مانده تخصیص: <b style={{color:selected.remain_to_pay!==0?'#e65100':'#1b5e20'}}>{numFmt(selected.remain_to_pay)||'۰'}</b></span>
          </>}
          <span style={{marginRight:'auto',color:'#888'}}>دابل‌کلیک روی ردیف = ریز سپرده</span>
        </div>
      </main>

      {/* ══ پاپ‌آپ سطح ۱: ریز سپرده‌ها ══ */}
      {rowsOpen && selected && (
        <div style={s.overlay} onClick={()=>{if(!alloc)setRowsOpen(false);}}>
          <div style={{...s.modal,maxWidth:'1180px'}} onClick={e=>e.stopPropagation()}>
            <div style={{...s.mHeader,background:'linear-gradient(135deg,#0d2137,#1a2f50)'}}>
              <div style={s.mTitle}>
                📄 ریز سپرده — {selected.contractor} | صورت وضعیت {toFa(selected.invoice_no)}
                {' '}| مبلغ {numFmt(selected.invoice_amount)}
              </div>
              <button onClick={()=>{setAlloc(null);setRowsOpen(false);}} style={s.closeBtn}>✕</button>
            </div>
            <div style={s.mBody}>
              {/* رادیو پیمانکار/قرارداد */}
              <div style={{display:'flex',gap:'16px',alignItems:'center',marginBottom:'10px',flexWrap:'wrap'}}>
                <label style={s.radio}>
                  <input type="radio" checked={depBy==='contractor'} onChange={()=>setDepBy('contractor')}/>
                  <span>پیمانکار (همه قراردادها)</span></label>
                <label style={s.radio}>
                  <input type="radio" checked={depBy==='contract'} onChange={()=>setDepBy('contract')}/>
                  <span>فقط این قرارداد</span></label>
                <span style={{marginRight:'auto',fontSize:'12px',color:'#555'}}>
                  جمع سپرده: <b>{numFmt(depSums.sum_deposit)||'۰'}</b> |
                  {' '}استرداد فعلی: <b style={{color:'#1565c0'}}>{numFmt(depSums.sum_current)||'۰'}</b> |
                  {' '}مانده: <b style={{color:'#1b5e20'}}>{numFmt(depSums.sum_remain)||'۰'}</b>
                </span>
              </div>
              <div style={{overflow:'auto',maxHeight:'58vh'}}>
                <table style={{width:'100%',borderCollapse:'collapse'}}>
                  <thead><tr>
                    {['قرارداد','تاریخ','موضوع','بستانکاری','سال','ردیف','سپرده',
                      'استردادی','استرداد سایر','استرداد فعلی','مانده','قبض','ملاحظات','']
                      .map(h=><th key={h} style={s.subTh}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {depLoading&&<tr><td colSpan={16} style={s.empty}>در حال بارگذاری...</td></tr>}
                    {!depLoading&&depRows.length===0&&
                      <tr><td colSpan={16} style={s.empty}>ریز سپرده‌ای برای این پیمانکار نیست</td></tr>}
                    {depRows.map((r,idx)=>(
                      <tr key={r.id} onDoubleClick={()=>openAlloc(r)}
                        style={{background:r.refund_current>0?'#e3f2fd':idx%2===0?'#fff':'#fffde7',
                          cursor:'pointer'}}>
                        <td style={s.subTd}>{toFa(r.contract_no)}</td>
                        <td style={{...s.subTd,direction:'ltr'}}>{toFa(r.contract_date)}</td>
                        <td style={{...s.subTd,textAlign:'right',maxWidth:'240px',overflow:'hidden',
                          textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={r.subject}>{r.subject}</td>
                        <td style={s.subTd}>{toFa(r.credit_no)}</td>
                        <td style={s.subTd}>{toFa(r.credit_year)}</td>
                        <td style={s.subTd}>{toFa(r.detail_row)}</td>
                        <td style={{...s.subTd,...s.num,color:'#1b5e20'}}>{numFmt(r.deposit_amount)}</td>
                        <td style={{...s.subTd,...s.num,color:'#6a1b9a'}}>{numFmt(r.refund_normal)}</td>
                        <td style={{...s.subTd,...s.num,color:'#8d6e63'}}>{numFmt(r.refund_other)}</td>
                        <td style={{...s.subTd,...s.num,color:'#1565c0'}}>{numFmt(r.refund_current)}</td>
                        <td style={{...s.subTd,...s.num,fontWeight:'700'}}>{numFmt(r.remain)}</td>
                        <td style={{...s.subTd,direction:'ltr'}}>{toFa(r.slip_no)}</td>
                        <td style={{...s.subTd,textAlign:'right',maxWidth:'180px',overflow:'hidden',
                          textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={r.remark}>{r.remark}</td>
                        <td style={s.subTd}>
                          {r.refund_blocked
                            ? <span title={r.block_reason} style={{color:'#c62828'}}>🚫</span>
                            : (vbf.can('BTNEDIT') && <button onClick={()=>openAlloc(r)} style={s.btnMini}>ثبت</button>)}
                        </td>
                      </tr>))}
                  </tbody>
                </table>
              </div>
              <div style={{fontSize:'11px',color:'#888',marginTop:'8px'}}>
                دابل‌کلیک روی ردیف یا دکمه «ثبت» = پنجره استرداد فعلی. آن پنجره قابل جابجایی است.
              </div>
            </div>
            <div style={s.mFooter}>
              <button onClick={()=>{setAlloc(null);setRowsOpen(false);}} style={s.btnCancel}>بستن</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ پاپ‌آپ سطح ۲: ثبت استرداد فعلی — قابل جابجایی ══ */}
      {alloc && (
        <div style={{...s.floatWrap,transform:`translate(${pos.x}px, ${pos.y}px)`}}>
          <div style={s.floatBox}>
            <div style={s.floatHeader} onMouseDown={startDrag}>
              <span style={{fontSize:'13px',fontWeight:'700',color:'#fff'}}>
                ✎ استرداد فعلی — بستانکاری {toFa(alloc.credit_no)}/{toFa(alloc.credit_year)} ردیف {toFa(alloc.detail_row)}
              </span>
              <span style={{fontSize:'11px',color:'rgba(255,255,255,0.6)'}}>⠿ برای جابجایی بکشید</span>
              <button onClick={()=>setAlloc(null)} style={s.closeBtn}>✕</button>
            </div>
            <div style={{padding:'16px'}}>
              {allocError&&<div style={s.errBox}>{allocError}</div>}
              <div style={s.allocInfo}>
                <div>مبلغ سپرده: <b>{numFmt(alloc.deposit_amount)}</b></div>
                <div>استرداد عادی: <b style={{color:'#6a1b9a'}}>{numFmt(alloc.refund_normal)||'۰'}</b></div>
                <div>استرداد سایر صورت وضعیت‌ها: <b style={{color:'#8d6e63'}}>{numFmt(alloc.refund_other)||'۰'}</b></div>
                <div>مانده قابل تخصیص: <b style={{color:'#1b5e20'}}>
                  {numFmt(alloc.remain+alloc.refund_current)||'۰'}</b></div>
              </div>
              <div style={{...s.row,marginTop:'12px'}}>
                <FInput label="مبلغ استرداد فعلی" name="allocPay" value={allocPay}
                  onChange={e=>setAllocPay(e.target.value)} type="number" dir="ltr" flex="0 0 48%"/>
                <FSelect label="برگه استرداد" name="allocRefund" value={allocRefund}
                  onChange={e=>setAllocRefund(Number(e.target.value))}
                  options={refundOpts} flex="0 0 48%"/>
              </div>
              <div style={{display:'flex',gap:'8px',marginBottom:'10px',flexWrap:'wrap'}}>
                {vbf.can('BTNEDIT') && <button onClick={()=>setAllocPay(alloc.remain+alloc.refund_current)}
                  style={s.btnMini}>کل مانده</button>}
                {selected && (vbf.can('BTNEDIT') && <button
                  onClick={()=>setAllocPay(Math.min(alloc.remain+alloc.refund_current,
                    Math.max(selected.remain_to_pay+alloc.refund_current,0)))}
                  style={s.btnMini}>تا سقف مانده صورت وضعیت</button>)}
                {vbf.can('BTNEDIT') && <button onClick={()=>setAllocPay(0)} style={{...s.btnMini,background:'#ffcdd2',color:'#b71c1c'}}>
                  حذف تخصیص</button>}
              </div>
              <div style={s.infoBox}>
                هر تخصیص یک ریز استرداد در برگه انتخاب‌شده ثبت می‌شود و در فرم «استرداد سپرده» هم دیده می‌شود.
                مبلغ صفر یعنی حذف تخصیص.
              </div>
            </div>
            <div style={{...s.mFooter,borderRadius:'0 0 14px 14px'}}>
              <button onClick={()=>setAlloc(null)} style={s.btnCancel}>لغو</button>
              {vbf.can('BTNEDIT') && <button onClick={saveAlloc} style={s.btnSave}>ثبت</button>}
            </div>
          </div>
        </div>
      )}

      {/* ══ مودال جدید / ویرایش سرگروه ══ */}
      {(modal==='new'||modal==='edit') && (
        <div style={s.overlay} onClick={()=>setModal(null)}>
          <div style={{...s.modal,maxWidth:'840px'}} onClick={e=>e.stopPropagation()}>
            <div style={s.mHeader}>
              <div style={s.mTitle}>{modal==='new'?'➕ ثبت صورت وضعیت جدید':'✎ ویرایش صورت وضعیت'}</div>
              <button onClick={()=>setModal(null)} style={s.closeBtn}>✕</button>
            </div>
            <div style={s.mBody}>
              {error&&<div style={s.errBox}>{error}</div>}
              <div style={s.row}>
                <div style={{flex:'0 0 100%',display:'flex',flexDirection:'column',gap:'4px',position:'relative'}}>
                  <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>قرارداد *</label>
                  <input value={form.contract_no?`${form.contract_no} — ${form.contractor}`:''}
                    placeholder="شماره قرارداد یا نام پیمانکار را تایپ کنید..."
                    onChange={e=>{setForm(p=>({...p,contract_no:e.target.value,contract_id:0}));doLookup(e.target.value);}}
                    onFocus={()=>doLookup('')} style={s.fI}/>
                  {lookup.length>0&&(
                    <div style={s.dropdown}>{lookup.map(r=>(
                      <div key={r.id} onClick={()=>{setForm(p=>({...p,contract_id:r.id,
                        contract_no:r.contract_no,contractor:r.contractor}));setLookup([]);}}
                        style={s.dropItem}>
                        <b>{toFa(r.contract_no)}</b> — {r.contractor}
                        <div style={{fontSize:'11px',color:'#888'}}>{r.subject}</div>
                      </div>))}</div>)}
                </div>
              </div>
              <div style={s.row}>
                <FInput label="صورت وضعیت: شماره *" name="invoice_no" value={form.invoice_no}
                  onChange={handleChange} flex="0 0 24%"/>
                <FDate label="تاریخ صورت وضعیت" value={form.invoice_date}
                  onChange={v=>setForm(p=>({...p,invoice_date:v}))} flex="0 0 24%"/>
                <FInput label="مبلغ صورت وضعیت *" name="invoice_amount" value={form.invoice_amount}
                  onChange={handleChange} type="number" dir="ltr" flex="0 0 28%"/>
                <FInput label="درصد" name="pitch" value={form.pitch}
                  onChange={handleChange} type="number" dir="ltr" flex="0 0 20%"/>
              </div>
              <div style={s.row}>
                <FInput label="علی الحساب" name="on_account" value={form.on_account}
                  onChange={handleChange} type="number" dir="ltr" flex="0 0 24%"/>
                <FInput label="پیش پرداخت" name="pre_payment" value={form.pre_payment}
                  onChange={handleChange} type="number" dir="ltr" flex="0 0 24%"/>
                <FInput label="ارزش افزوده" name="added_tax" value={form.added_tax}
                  onChange={handleChange} type="number" dir="ltr" flex="0 0 24%"/>
                <FInput label="قابل پرداخت" name="final_pay" value={form.final_pay}
                  onChange={handleChange} type="number" dir="ltr" flex="0 0 24%"/>
              </div>
              <div style={s.netBox}>
                خالص = مبلغ صورت وضعیت + اضافات − کسورات − علی الحساب − پیش پرداخت
                {' = '}<b style={{fontSize:'15px',color:'#6a1b9a'}}>{numFmt(formNet)||'۰'}</b> ریال
                {modal==='edit' && (
                  <div style={{fontSize:'11px',marginTop:'4px'}}>
                    اضافات <b>{numFmt(formAdd)||'۰'}</b> و کسورات <b>{numFmt(formDed)||'۰'}</b>
                    {' '}با دکمه‌های بالای صفحه ویرایش می‌شوند.
                  </div>)}
                {modal==='new' && (
                  <div style={{fontSize:'11px',marginTop:'4px'}}>
                    پس از ثبت، اضافات و کسورات را با دکمه‌های بالای صفحه اضافه کنید.
                  </div>)}
              </div>
              <div style={s.row}>
                <FInput label="پرداختی از محل سپرده" name="deposit" value={form.deposit}
                  onChange={handleChange} type="number" dir="ltr" flex="0 0 32%"/>
                <FInput label="ضمانتنامه قرارداد جدید" name="zemanat_amount"
                  value={form.zemanat_amount} onChange={handleChange}
                  type="number" dir="ltr" flex="0 0 32%"/>
                <FInput label="سال قرارداد جدید" name="new_contract_year"
                  value={form.new_contract_year} onChange={handleChange}
                  type="number" dir="ltr" flex="0 0 32%"/>
              </div>
              <div style={s.row}>
                <FInput label="درخواست: شماره" name="request_no" value={form.request_no}
                  onChange={handleChange} flex="0 0 24%"/>
                <FDate label="تاریخ درخواست" value={form.request_date}
                  onChange={v=>setForm(p=>({...p,request_date:v}))} flex="0 0 24%"/>
                <FInput label="نامه ارسالی: شماره" name="letter_no" value={form.letter_no}
                  onChange={handleChange} flex="0 0 24%"/>
                <FDate label="تاریخ نامه" value={form.letter_date}
                  onChange={v=>setForm(p=>({...p,letter_date:v}))} flex="0 0 24%"/>
              </div>
              <div style={s.row}>
                <FInput label="شماره اقدام" name="act_no" value={form.act_no}
                  onChange={handleChange} flex="0 0 32%"/>
                <FInput label="توضیحات" name="remark" value={form.remark}
                  onChange={handleChange} flex="1"/>
              </div>
              <div style={s.row}>
                <label style={{display:'inline-flex',alignItems:'center',gap:'6px',fontSize:'12px',cursor:'pointer'}}>
                  <input type="checkbox" checked={!!form.is_showable}
                    onChange={e=>setForm(p=>({...p,is_showable:e.target.checked}))}/>
                  قابل نمایش در سایر زیرسیستمها
                </label>
              </div>
              {modal==='edit'&&selected?.alloc_count>0&&(
                <div style={s.warnBox}>
                  ⚠️ این صورت وضعیت {toFa(selected.alloc_count)} تخصیص استرداد دارد
                  (جمع {numFmt(selected.allocated)} ریال).
                </div>)}
            </div>
            <div style={s.mFooter}>
              <button onClick={()=>setModal(null)} style={s.btnCancel}>لغو</button>
              {vbf.can('BTNOK') && <button onClick={handleSave} style={s.btnSave}>ثبت</button>}
            </div>
          </div>
        </div>
      )}

      {/* ══ مودال حذف ══ */}
      {modal==='delete'&&selected&&(
        <div style={s.overlay} onClick={()=>setModal(null)}>
          <div style={{...s.modal,maxWidth:'500px'}} onClick={e=>e.stopPropagation()}>
            <div style={{...s.mHeader,background:'linear-gradient(135deg,#b71c1c,#c62828)'}}>
              <div style={s.mTitle}>⚠️ تأیید حذف</div>
              <button onClick={()=>setModal(null)} style={s.closeBtn}>✕</button></div>
            <div style={s.mBody}>
              {error&&<div style={s.errBox}>{error}</div>}
              <p style={{fontSize:'14px',lineHeight:'2'}}>
                آیا از حذف صورت وضعیت <b>{toFa(selected.invoice_no)}</b> قرارداد
                {' '}<b>{toFa(selected.contract_no)}</b> ({selected.contractor}) به
                مبلغ <b>{numFmt(selected.invoice_amount)}</b> ریال مطمئن هستید؟</p>
              {(selected.addition_count>0||selected.deduction_count>0) &&
                <p style={{color:'#e65100',fontSize:'12px'}}>
                  ⚠️ {toFa(selected.addition_count)} قلم اضافات و {toFa(selected.deduction_count)}
                  {' '}قلم کسورات این صورت وضعیت هم حذف می‌شوند.</p>}
              {selected.alloc_count>0&&<p style={{color:'#c62828',fontWeight:'700'}}>
                ⚠️ برای حذف این صورت وضعیت ابتدا {toFa(selected.alloc_count)} تخصیص استرداد آن را حذف کنید.</p>}
            </div>
            <div style={s.mFooter}>
              <button onClick={()=>setModal(null)} style={s.btnCancel}>لغو</button>
              {selected.alloc_count===0&&(vbf.can('BTNDELETE') && <button onClick={handleDelete}
                style={{...s.btnSave,background:'linear-gradient(135deg,#b71c1c,#c62828)'}}>حذف</button>)}
            </div>
          </div>
        </div>
      )}

      {/* ══ مودال اضافات یا کسورات ══ */}
      {itemModal && selected && (
        <div style={s.overlay} onClick={()=>setItemModal(null)}>
          <div style={{...s.modal,maxWidth:'820px'}} onClick={e=>e.stopPropagation()}>
            <div style={{...s.mHeader,background:itemModal==='addition'
              ?'linear-gradient(135deg,#1b5e20,#2e7d32)'
              :'linear-gradient(135deg,#b71c1c,#c62828)'}}>
              <div style={s.mTitle}>
                {itemModal==='addition'?'＋ اضافات':'− کسورات'} صورت وضعیت
                {' '}{toFa(selected.invoice_no)} — {selected.contractor}
              </div>
              <button onClick={()=>setItemModal(null)} style={s.closeBtn}>✕</button>
            </div>
            <div style={s.mBody}>
              {itemError&&<div style={s.errBox}>{itemError}</div>}
              <div style={s.infoBox}>
                مبلغ صورت وضعیت: <b>{numFmt(selected.invoice_amount)}</b> ریال.
                اگر درصد وارد کنید و مبلغ را خالی بگذارید، مبلغ از روی همین عدد محاسبه می‌شود.
              </div>

              {/* فرم ثبت */}
              <div style={s.itemForm}>
                <div style={s.row}>
                  <FSelect label={itemModal==='addition'?'نوع اضافات':'نوع کسورات'}
                    name="kind_id" value={itemForm.kind_id}
                    onChange={e=>setItemForm(p=>({...p,kind_id:Number(e.target.value)}))}
                    options={[{value:0,label:'— انتخاب کنید —'},
                      ...itemTypes.map(x=>({value:x.id,label:x.title}))]} flex="0 0 40%"/>
                  <FInput label="مبلغ" name="amount" value={itemForm.amount}
                    onChange={e=>setItemForm(p=>({...p,amount:e.target.value}))}
                    type="number" dir="ltr" flex="0 0 26%"/>
                  <FInput label="درصد" name="percent" value={itemForm.percent}
                    onChange={e=>setItemForm(p=>({...p,percent:e.target.value}))}
                    type="number" dir="ltr" flex="0 0 14%"/>
                  <div style={{display:'flex',alignItems:'flex-end',gap:'6px',flex:'0 0 16%'}}>
                    {vbf.can('BTNNEW') && <button onClick={saveItem} style={s.btnSave}>
                      {itemForm.id?'ویرایش':'افزودن'}</button>}
                    {itemForm.id>0 && <button
                      onClick={()=>setItemForm({id:0,kind_id:0,amount:'',percent:''})}
                      style={s.btnCancel}>لغو</button>}
                  </div>
                </div>
              </div>

              {/* فهرست */}
              <div style={{overflow:'auto',maxHeight:'42vh',marginTop:'12px'}}>
                <table style={{width:'100%',borderCollapse:'collapse'}}>
                  <thead><tr>
                    {['ردیف','نوع','درصد','مبلغ','']
                      .map(h=><th key={h} style={s.subTh}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {items.length===0&&<tr><td colSpan={5} style={s.empty}>
                      قلمی ثبت نشده است</td></tr>}
                    {items.map((r,idx)=>(
                      <tr key={r.id} style={{background:itemForm.id===r.id?'#e3f2fd'
                        :idx%2===0?'#fff':'#fffde7'}}>
                        <td style={s.subTd}>{toFa(idx+1)}</td>
                        <td style={{...s.subTd,textAlign:'right'}}>{r.kind_title}</td>
                        <td style={s.subTd}>{r.percent?toFa(r.percent):''}</td>
                        <td style={{...s.subTd,...s.num,
                          color:itemModal==='addition'?'#1b5e20':'#c62828'}}>
                          {numFmt(r.amount)}</td>
                        <td style={s.subTd}>
                          {vbf.can('BTNNEW') && <button onClick={()=>setItemForm({id:r.id,kind_id:r.kind_id,
                            amount:r.amount,percent:r.percent})} style={s.btnMini}>✎</button>}
                          {vbf.can('BTNDELETE') && <button onClick={()=>deleteItem(r.id)}
                            style={{...s.btnMini,background:'#ffcdd2',color:'#b71c1c',
                              borderColor:'#ef9a9a',marginRight:'4px'}}>✕</button>}
                        </td>
                      </tr>))}
                  </tbody>
                </table>
              </div>

              <div style={{...s.summary,margin:'10px 0 0'}}>
                <span>تعداد: <b>{toFa(items.length)}</b></span>
                <span>جمع {itemModal==='addition'?'اضافات':'کسورات'}:
                  {' '}<b style={{color:itemModal==='addition'?'#1b5e20':'#c62828'}}>
                    {numFmt(itemSum)||'۰'}</b> ریال</span>
                <span style={{marginRight:'auto',color:'#888'}}>
                  با هر تغییر، مبلغ خالص صورت وضعیت دوباره محاسبه می‌شود</span>
              </div>
            </div>
            <div style={s.mFooter}>
              <button onClick={()=>setItemModal(null)} style={s.btnCancel}>بستن</button>
            </div>
          </div>
        </div>
      )}

      {toast&&<div style={s.toast}>{toast}</div>}
    </div>
  );
}

const s = {
  page:{height:'100vh',overflow:'hidden',background:'#f0f2f7',fontFamily:"'Vazirmatn',Tahoma,sans-serif",direction:'rtl',display:'flex',flexDirection:'column'},
  header:{background:'linear-gradient(135deg,#0a1628,#1a2f50)',padding:'10px 24px',display:'flex',justifyContent:'space-between',alignItems:'center',boxShadow:'0 2px 16px rgba(0,0,0,0.3)',flexShrink:0},
  hLeft:{display:'flex',alignItems:'center',gap:'12px'},
  backBtn:{padding:'6px 14px',background:'rgba(255,255,255,0.12)',color:'#fff',border:'1px solid rgba(255,255,255,0.25)',borderRadius:'7px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px'},
  hLogo:{width:'40px',height:'40px',background:'rgba(200,168,75,0.12)',border:'1px solid rgba(200,168,75,0.3)',borderRadius:'9px',display:'flex',alignItems:'center',justifyContent:'center'},
  hTitle:{fontSize:'15px',fontWeight:'700',color:'#fff'},
  hSub:{fontSize:'11px',color:'rgba(255,255,255,0.55)'},
  userName:{fontSize:'13px',color:'rgba(255,255,255,0.85)'},
  main:{padding:'8px 20px',flex:1,display:'flex',flexDirection:'column',overflow:'auto',minHeight:0},
  toolbar:{display:'flex',alignItems:'center',gap:'8px',background:'#fff',padding:'8px 14px',borderRadius:'10px',boxShadow:'0 1px 6px rgba(0,0,0,0.08)',marginBottom:'6px',flexWrap:'wrap',flexShrink:0},
  toolbar2:{display:'flex',alignItems:'flex-end',gap:'12px',background:'#fff',padding:'8px 14px',borderRadius:'10px',boxShadow:'0 1px 6px rgba(0,0,0,0.08)',marginBottom:'6px',flexWrap:'wrap',flexShrink:0},
  toolBtns:{display:'flex',gap:'7px',flexWrap:'wrap'},
  radio:{display:'flex',alignItems:'center',gap:'5px',fontSize:'12px',fontWeight:'600',color:'#444',cursor:'pointer'},
  fL:{fontSize:'11px',fontWeight:'600',color:'#555',marginBottom:'3px'},
  fI:{padding:'8px 12px',border:'1.5px solid #e0e0e0',borderRadius:'8px',fontSize:'13px',fontFamily:"'Vazirmatn',Tahoma,sans-serif",outline:'none',direction:'rtl',textAlign:'right',boxSizing:'border-box',width:'100%'},
  btnNew:{padding:'7px 16px',background:'linear-gradient(135deg,#1b5e20,#2e7d32)',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px',fontWeight:'700'},
  btnEdit:{padding:'7px 16px',background:'linear-gradient(135deg,#1a237e,#283593)',color:'#fff',border:'none',borderRadius:'8px',fontFamily:'inherit',fontSize:'13px',fontWeight:'700',cursor:'pointer'},
  btnDel:{padding:'7px 16px',background:'linear-gradient(135deg,#b71c1c,#c62828)',color:'#fff',border:'none',borderRadius:'8px',fontFamily:'inherit',fontSize:'13px',fontWeight:'700',cursor:'pointer'},
  btnRows:{padding:'7px 16px',background:'linear-gradient(135deg,#e65100,#f57c00)',color:'#fff',border:'none',borderRadius:'8px',fontFamily:'inherit',fontSize:'13px',fontWeight:'700',cursor:'pointer'},
  btnAdd:{padding:'7px 16px',background:'linear-gradient(135deg,#1b5e20,#2e7d32)',color:'#fff',border:'none',borderRadius:'8px',fontFamily:'inherit',fontSize:'13px',fontWeight:'700',cursor:'pointer'},
  btnDed:{padding:'7px 16px',background:'linear-gradient(135deg,#b71c1c,#c62828)',color:'#fff',border:'none',borderRadius:'8px',fontFamily:'inherit',fontSize:'13px',fontWeight:'700',cursor:'pointer'},
  itemForm:{background:'#fafafa',border:'1px solid #eee',borderRadius:'10px',padding:'14px 14px 2px'},
  btnMini:{padding:'4px 10px',background:'#e3f2fd',color:'#0d47a1',border:'1px solid #90caf9',borderRadius:'6px',cursor:'pointer',fontFamily:'inherit',fontSize:'11px',fontWeight:'700'},
  tableWrap:{overflowX:'auto',overflowY:'auto',borderRadius:'10px',boxShadow:'0 2px 8px rgba(0,0,0,0.1)',background:'#fff',flex:1,minHeight:'260px'},
  td:{padding:'6px 8px',borderBottom:'1px solid #f0f0f0',textAlign:'center',fontSize:'12px',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'},
  num:{direction:'ltr',textAlign:'left',fontWeight:'700'},
  empty:{textAlign:'center',padding:'40px',color:'#999',fontSize:'13px'},
  summary:{display:'flex',gap:'18px',flexWrap:'wrap',background:'#fff',padding:'8px 14px',borderRadius:'10px',boxShadow:'0 1px 6px rgba(0,0,0,0.08)',margin:'6px 0',fontSize:'12px',color:'#444',flexShrink:0},
  subTh:{padding:'7px 9px',background:'#0d2137',color:'#C8A84B',fontSize:'11px',fontWeight:'700',whiteSpace:'nowrap',position:'sticky',top:0,zIndex:2},
  subTd:{padding:'6px 8px',borderBottom:'1px solid #f0f0f0',textAlign:'center',fontSize:'12px',whiteSpace:'nowrap'},
  overlay:{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px'},
  modal:{background:'#fff',borderRadius:'16px',width:'100%',maxWidth:'760px',maxHeight:'92vh',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.4)'},
  mHeader:{display:'flex',justifyContent:'space-between',alignItems:'center',gap:'10px',padding:'14px 20px',background:'linear-gradient(135deg,#0a1628,#1a2f50)',borderRadius:'16px 16px 0 0'},
  mTitle:{fontSize:'14px',fontWeight:'700',color:'#fff'},
  closeBtn:{background:'rgba(255,255,255,0.15)',border:'1px solid rgba(255,255,255,0.3)',color:'#fff',width:'30px',height:'30px',borderRadius:'50%',cursor:'pointer',fontSize:'13px',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0},
  mBody:{overflowY:'auto',flex:1,padding:'18px'},
  mFooter:{display:'flex',justifyContent:'flex-end',gap:'10px',padding:'12px 18px',borderTop:'1px solid #f0f2f7',background:'#f8f9fa',borderRadius:'0 0 16px 16px'},
  row:{display:'flex',gap:'12px',marginBottom:'12px',flexWrap:'wrap'},
  errBox:{background:'#fff3f3',border:'1px solid #ffcdd2',borderRadius:'8px',padding:'10px 14px',color:'#c62828',marginBottom:'10px',fontSize:'13px'},
  warnBox:{background:'#fff8e1',border:'1px solid #ffe082',borderRadius:'8px',padding:'9px 14px',color:'#e65100',marginBottom:'8px',fontSize:'12px'},
  infoBox:{background:'#e3f2fd',border:'1px solid #90caf9',borderRadius:'8px',padding:'9px 14px',color:'#0d47a1',fontSize:'11px',lineHeight:'1.9'},
  netBox:{background:'#f3e5f5',border:'1px solid #ce93d8',borderRadius:'8px',padding:'10px 14px',color:'#4a148c',fontSize:'12px',marginBottom:'12px'},
  allocInfo:{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px 14px',fontSize:'12px',color:'#444',background:'#fafafa',border:'1px solid #eee',borderRadius:'8px',padding:'10px 14px'},
  /* پاپ‌آپ شناور سطح ۲ */
  floatWrap:{position:'fixed',top:'50%',left:'50%',marginTop:'-200px',marginLeft:'-260px',zIndex:1200},
  floatBox:{width:'520px',background:'#fff',borderRadius:'14px',boxShadow:'0 24px 70px rgba(0,0,0,0.5)',border:'2px solid #1a2f50',direction:'rtl'},
  floatHeader:{display:'flex',alignItems:'center',gap:'10px',justifyContent:'space-between',padding:'10px 14px',background:'linear-gradient(135deg,#1a237e,#283593)',borderRadius:'12px 12px 0 0',cursor:'move',userSelect:'none'},
  dropdown:{position:'absolute',top:'100%',left:0,right:0,background:'#fff',border:'1px solid #ddd',borderRadius:'8px',maxHeight:'220px',overflowY:'auto',zIndex:50,boxShadow:'0 6px 20px rgba(0,0,0,0.18)'},
  dropItem:{padding:'8px 11px',fontSize:'12px',cursor:'pointer',borderBottom:'1px solid #f5f5f5',textAlign:'right'},
  btnCancel:{padding:'8px 18px',background:'#e0e0e0',color:'#333',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px'},
  btnSave:{padding:'8px 22px',background:'linear-gradient(135deg,#1b5e20,#2e7d32)',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px',fontWeight:'700'},
  toast:{position:'fixed',bottom:'24px',right:'24px',background:'#1b5e20',color:'#fff',padding:'12px 24px',borderRadius:'10px',fontSize:'14px',fontWeight:'600',zIndex:2000,boxShadow:'0 4px 16px rgba(0,0,0,0.25)'},
};